import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { STAGES, WORK_TYPES, CAN_CREATE_PROJECT, DEAL_VIEW } from "@/lib/constants";
import { Loader2, Search, PlusCircle, Filter } from "lucide-react";
import { computeTotals, MasterSummary, MasterTotals, ProjectCard } from "@/components/ProjectMasterParts";

const FLAG_LABEL = {
  no_pic: "Proyek Tanpa Penanggung Jawab",
  overdue: "Proyek Melewati Target",
  no_report_today: "Proyek Tanpa Laporan Hari Ini",
};

export default function Projects() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const stage = params.get("stage") || "";
  const work_type = params.get("work_type") || "";
  const flag = params.get("flag") || "";
  const canViewDeal = user && DEAL_VIEW.includes(user.role);
  const [fin, setFin] = useState({ rows: {}, profit: false });
  const [all, setAll] = useState([]);
  useEffect(() => {
    api.get("/projects").then(({ data }) => setAll(data)).catch(() => {});
    if (canViewDeal) { api.get("/project-master/finance").then(({ data }) => setFin(data)).catch(() => {}); }
  }, [canViewDeal]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qp = {};
      if (params.get("q")) qp.q = params.get("q");
      if (stage) qp.stage = stage;
      if (work_type) qp.work_type = work_type;
      if (flag) qp.flag = flag;
      const { data } = await api.get("/projects", { params: qp });
      setProjects(data);
    } finally { setLoading(false); }
  }, [params, stage, work_type, flag]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setQ(params.get("q") || ""); }, [params]);

  const setParam = (k, v) => {
    const np = new URLSearchParams(params);
    if (v && v !== "all") np.set(k, v); else np.delete(k);
    if (k !== "flag") np.delete("flag");
    setParams(np);
  };

  const submitSearch = (e) => { e.preventDefault(); setParam("q", q); };
  const filtered = Boolean(params.get("q") || stage || work_type || flag);
  const tFilter = computeTotals(projects, fin.rows);
  const tAll = computeTotals(all, fin.rows);

  return (
    <div className="space-y-4" data-testid="projects-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">{flag ? FLAG_LABEL[flag] : "Project Master"}</h1>
          <p className="text-sm text-muted-foreground">{projects.length} proyek {stage && `· ${stage}`}</p>
        </div>
        {user && CAN_CREATE_PROJECT.includes(user.role) && (
          <Button onClick={() => navigate("/projects/new")} data-testid="new-project-btn" className="bg-primary hover:bg-red-700">
            <PlusCircle size={16} className="mr-1.5" /> Buat Proyek Baru
          </Button>
        )}
      </div>

      <MasterSummary t={tAll} canDeal={canViewDeal} />

      <Card className="p-3">
        <form onSubmit={submitSearch} className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="project-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari Project ID, No. Polisi, customer, penanggung jawab..." className="pl-9" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select value={stage || "all"} onValueChange={(v) => setParam("stage", v)}>
              <SelectTrigger className="w-[180px]" data-testid="filter-stage"><SelectValue placeholder="Tahap" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Tahap</SelectItem>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={work_type || "all"} onValueChange={(v) => setParam("work_type", v)}>
              <SelectTrigger className="w-[150px]" data-testid="filter-worktype"><SelectValue placeholder="Pekerjaan" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Pekerjaan</SelectItem>{WORK_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </form>
      </Card>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div> : (
        projects.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground"><Filter className="mx-auto mb-2 opacity-40" /> Tidak ada proyek ditemukan.</Card>
        ) : (
          <>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
              {projects.map((p) => (
                <ProjectCard key={p.id} p={{ ...p, stageBadge: <StatusBadge status={p.stage} />, targetLabel: fmtDate(p.target_date) }}
                  f={fin.rows[p.id]} canDeal={canViewDeal} profit={fin.profit} onClick={() => navigate(`/projects/${p.id}`)} />
              ))}
            </div>
            {filtered && <MasterTotals title="Total sesuai filter" t={tFilter} canDeal={canViewDeal} profit={fin.profit} testid="pm-total-filter" />}
            <MasterTotals title="Grand Total semua proyek" t={tAll} canDeal={canViewDeal} profit={fin.profit} testid="pm-total-grand" />
          </>
        )
      )}
    </div>
  );
}
